// US-ROUTINE-06 -- Guardian manages repeating checklists.
//
// This is an action screen, not a settings page. Its structure follows the
// supplied Claude Design `A_Routines` frame: a compact back header, a short
// explanation of the concept, routine cards, and a dashed creation card.

import {
  type AccountListEntry,
  type BucketEntry,
  type RoutineEntry,
  accountsList,
  bucketsList,
  routineDelete,
  routinePause,
  routineResume,
  routinesListForGuardian,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ReportContentButton } from '../components/ReportContentButton';
import { isFamilyContentHidden } from '../state/familyContentVisibility';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type Props = {
  sessionId: string;
  currency: string;
  onAddRoutine: (children: AccountListEntry[], buckets: BucketEntry[]) => void;
  onEditRoutine: (task: RoutineEntry, child: AccountListEntry, childBuckets: BucketEntry[]) => void;
  onViewHistory: (task: RoutineEntry) => void;
  onBack: () => void;
  topLevel?: boolean;
};

const scheduleLabel = (task: RoutineEntry) => {
  const days = [...task.scheduleDays].sort((a, b) => a - b);
  const dayLabel =
    days.length === 7
      ? 'Every day'
      : days.length === 5 && days.every((day, index) => day === index + 1)
        ? 'School days'
        : days.length === 2 && days[0] === 0 && days[1] === 6
          ? 'Weekends'
          : days.length > 0
            ? days.map((day) => DAY_LABELS[day]).join(', ')
            : 'Choose days';
  return task.repeatIntervalWeeks > 1
    ? `${dayLabel} · every ${task.repeatIntervalWeeks} weeks`
    : dayLabel;
};

const formatMoney = (currency: string, minor: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(minor / 100);

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

export const RoutineManagementScreen: Component<Props> = (props) => {
  const [children] = createResource(async () => {
    const result = await accountsList();
    return result.ok ? result.value.filter((account) => account.roleCode === 'child') : [];
  });
  const [buckets] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    return result.ok ? result.value : [];
  });
  const [routines, { refetch }] = createResource(
    () => props.sessionId,
    (sid) => routinesListForGuardian(sid).then((result) => (result.ok ? result.value : [])),
  );

  const [busy, setBusy] = createSignal<string | null>(null);
  const [error, setError] = createSignal<string | null>(null);
  const [pausingId, setPausingId] = createSignal<number | null>(null);
  const [pauseUntil, setPauseUntil] = createSignal('');

  const childFor = (task: RoutineEntry) =>
    (children() ?? []).find((account) => account.accountId === task.childAccountId);
  const childBuckets = (childAccountId: number) =>
    (buckets() ?? []).filter((bucket) => bucket.childAccountId === childAccountId);

  const handlePause = async (task: RoutineEntry) => {
    setBusy(`pause-${task.recurringTaskId}`);
    setError(null);
    const result = await routinePause(props.sessionId, task.recurringTaskId, pauseUntil() || null);
    setBusy(null);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    setPausingId(null);
    void refetch();
  };

  const handleResume = async (task: RoutineEntry) => {
    setBusy(`resume-${task.recurringTaskId}`);
    setError(null);
    const result = await routineResume(props.sessionId, task.recurringTaskId);
    setBusy(null);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    void refetch();
  };

  const handleDelete = async (task: RoutineEntry) => {
    if (!confirm(`Delete "${task.taskName}"? This cannot be undone. Existing history is kept.`))
      return;
    setBusy(`delete-${task.recurringTaskId}`);
    setError(null);
    const result = await routineDelete(props.sessionId, task.recurringTaskId);
    setBusy(null);
    if (!result.ok) {
      setError(result.error.message);
      return;
    }
    void refetch();
  };

  return (
    <ActionScreen
      eyebrow="Repeating checklists"
      title="Routines"
      onBack={props.onBack}
      showBack={!props.topLevel}
      class="action-screen--routine-management"
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          disabled={children.loading || (children()?.length ?? 0) === 0}
          onClick={() => props.onAddRoutine(children() ?? [], buckets() ?? [])}
          data-testid="add-routine-btn"
        >
          + New routine
        </button>
      }
    >
      <div class="routine-management-screen" data-testid="routine-management-screen">
        <Show when={error()}>
          <div class="form-error" role="alert">
            {error()}
          </div>
        </Show>

        <section class="routine-explainer" aria-label="What is a routine?">
          <span class="routine-explainer-icon" aria-hidden="true">
            ↻
          </span>
          <p>
            A routine is a checklist that repeats — bedtime, morning, tidy-up. Kids tick each step,
            then send the whole thing for checking.
          </p>
        </section>

        <Show when={routines.loading}>
          <p class="routine-loading">Loading routines…</p>
        </Show>

        <Show when={!routines.loading && (routines()?.length ?? 0) === 0}>
          <section class="routine-empty" aria-live="polite">
            <strong>No routines yet</strong>
            <p>Start with a familiar checklist your child can complete step by step.</p>
          </section>
        </Show>

        <Show when={(routines()?.length ?? 0) > 0}>
          <ul class="routine-card-list">
            <For
              each={(routines() ?? []).filter(
                (task) => !isFamilyContentHidden(`routine:${task.recurringTaskId}`),
              )}
            >
              {(task) => {
                const child = () => childFor(task);
                return (
                  <li
                    class="routine-card"
                    classList={{ 'routine-card--paused': task.isPaused }}
                    data-testid={`routine-row-${task.recurringTaskId}`}
                  >
                    <div class="routine-card-summary">
                      <span class="routine-card-icon" aria-hidden="true">
                        ↻
                      </span>
                      <div class="routine-card-copy">
                        <strong>{task.taskName}</strong>
                        <span>
                          {task.subTasks.length} step{task.subTasks.length === 1 ? '' : 's'} ·{' '}
                          {scheduleLabel(task)}
                        </span>
                      </div>
                      <Show when={child()}>
                        {(linkedChild) => (
                          <span
                            class="routine-child-avatar routine-card-child"
                            title={linkedChild().displayName}
                          >
                            {initials(linkedChild().displayName)}
                          </span>
                        )}
                      </Show>
                      <Show when={task.rewardMinorUnits > 0}>
                        <span class="routine-card-pay">
                          {formatMoney(props.currency, task.rewardMinorUnits)}
                        </span>
                      </Show>
                    </div>

                    <Show when={task.isPaused}>
                      <span class="routine-badge routine-badge--paused">
                        {task.pauseUntilDate ? `Paused until ${task.pauseUntilDate}` : 'Paused'}
                      </span>
                    </Show>

                    <Show when={pausingId() === task.recurringTaskId}>
                      <div class="routine-pause-panel">
                        <label for={`routine-pause-${task.recurringTaskId}`}>
                          Pause until (leave blank to pause indefinitely)
                        </label>
                        <input
                          id={`routine-pause-${task.recurringTaskId}`}
                          type="date"
                          value={pauseUntil()}
                          onInput={(event) => setPauseUntil(event.currentTarget.value)}
                        />
                        <div class="routine-pause-actions">
                          <button
                            type="button"
                            class="primary"
                            disabled={!!busy()}
                            onClick={() => handlePause(task)}
                          >
                            Confirm pause
                          </button>
                          <button type="button" class="ghost" onClick={() => setPausingId(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </Show>

                    <div class="routine-card-actions" aria-label={`Manage ${task.taskName}`}>
                      <ReportContentButton
                        class="routine-text-button"
                        context={{
                          contentKind: 'routine',
                          contentLocalId: `routine:${task.recurringTaskId}`,
                          label: `routine “${task.taskName}”`,
                          contentSnapshot: {
                            recurringTaskId: task.recurringTaskId,
                            childAccountId: task.childAccountId,
                            childDisplayName: child()?.displayName ?? null,
                            taskName: task.taskName,
                            taskNotes: task.taskNotes,
                            scheduleDays: task.scheduleDays,
                            repeatIntervalWeeks: task.repeatIntervalWeeks,
                            steps: task.subTasks.map((step) => ({
                              subTaskId: step.subTaskId,
                              sortOrder: step.sortOrder,
                              subTaskName: step.subTaskName,
                            })),
                          },
                        }}
                      />
                      <button
                        type="button"
                        class="routine-text-button"
                        disabled={!!busy()}
                        onClick={() => props.onViewHistory(task)}
                      >
                        History
                      </button>
                      <button
                        type="button"
                        class="routine-text-button"
                        disabled={!!busy() || !child()}
                        onClick={() => {
                          const linkedChild = child();
                          if (linkedChild)
                            props.onEditRoutine(
                              task,
                              linkedChild,
                              childBuckets(task.childAccountId),
                            );
                        }}
                        data-testid={`edit-routine-${task.recurringTaskId}`}
                      >
                        Edit
                      </button>
                      <Show when={!task.isPaused}>
                        <button
                          type="button"
                          class="routine-text-button"
                          disabled={!!busy()}
                          onClick={() => {
                            setPausingId(task.recurringTaskId);
                            setPauseUntil('');
                          }}
                        >
                          Pause
                        </button>
                      </Show>
                      <Show when={task.isPaused}>
                        <button
                          type="button"
                          class="routine-text-button"
                          disabled={!!busy()}
                          onClick={() => handleResume(task)}
                        >
                          Resume
                        </button>
                      </Show>
                      <button
                        type="button"
                        class="routine-text-button routine-text-button--danger"
                        disabled={!!busy()}
                        onClick={() => handleDelete(task)}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                );
              }}
            </For>
          </ul>
        </Show>
      </div>
    </ActionScreen>
  );
};
